/**
===================================================
Module: frontend.src.services.api.ts
Description: API service for backend communication and authentication
Owner: Kawada Technosystem Co Ltd
All Rights Reserved
This script is proprietary and confidential.
Unauthorized copying, distribution, or modification of this
file, via any medium, is strictly prohibited.
===================================================
*/

import {
  User,
  SystemSettings,
  Project,
  Session,
  CreateProjectRequest,
  CreateSessionRequest,
} from "../types/index";

// Import types only - we'll import the service lazily to avoid circular dependency
import type { CognitoAuthResult } from "./cognito";

export const EXTERNAL_LINKS = {
  HELP_MANUAL: "https://www.kts-download.net/download/ai/arcana-trial_manual.pdf",
};

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:7890/api";
export const POLLING_INTERVAL_MS = 10000;

// Lazy getter for cognitoService to avoid circular dependency
let _cognitoService: typeof import("./cognito").cognitoService | null = null;
const getCognitoService = async () => {
  if (!_cognitoService) {
    const module = await import("./cognito");
    _cognitoService = module.cognitoService;
  }
  return _cognitoService;
};

class ApiService {
  private async getToken(): Promise<string | null> {
    try {
      // Try to get fresh token from Cognito session
      const cognito = await getCognitoService();
      const token = await cognito.getAccessToken();
      if (token) {
        return token;
      }
    } catch (e) {
      // Cognito not available, fall back to stored token
    }
    // Fallback to stored token
    return localStorage.getItem("access_token");
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    const token = await this.getToken();

    const config: RequestInit = {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
    };

    const response = await fetch(url, config);

    if (!response.ok) {
      if (response.status === 401) {
        this.logout();
        window.location.href = "/login";
        throw new Error("Authentication failed. Please login again.");
      }

      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { detail: errorText };
      }
      throw new Error(errorData.detail || `API Error: ${response.status}`);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }

  // Auth endpoints - now using Cognito
  async login(email: string, password: string): Promise<CognitoAuthResult> {
    const cognito = await getCognitoService();
    // Authenticate with Cognito
    const authResult = await cognito.signIn(email, password);

    // Sync user to backend DB
    try {
      await this.syncUser();
    } catch (error) {
      console.warn("User sync failed:", error);
      // Don't fail login if sync fails - user can still use the app
    }

    return authResult;
  }

  async register(
    email: string,
    password: string,
    firstName?: string,
    lastName?: string,
    phoneNumber?: string
  ): Promise<{ message: string; userConfirmed: boolean }> {
    const cognito = await getCognitoService();
    const result = await cognito.signUp(
      email,
      password,
      firstName,
      lastName,
      phoneNumber
    );

    return {
      message: result.userConfirmed
        ? "Registration successful! You can now sign in."
        : "Registration successful! Please check your email for a verification code.",
      userConfirmed: result.userConfirmed,
    };
  }

  async confirmRegistration(email: string, code: string): Promise<void> {
    const cognito = await getCognitoService();
    await cognito.confirmSignUp(email, code);
  }

  async resendVerificationCode(email: string): Promise<void> {
    const cognito = await getCognitoService();
    await cognito.resendConfirmationCode(email);
  }

  async forgotPassword(email: string): Promise<{ message: string }> {
    const cognito = await getCognitoService();
    await cognito.forgotPassword(email);
    return { message: "Password reset code sent to your email" };
  }

  async resetPassword(
    email: string,
    code: string,
    newPassword: string
  ): Promise<{ message: string }> {
    const cognito = await getCognitoService();
    await cognito.confirmForgotPassword(email, code, newPassword);
    return { message: "Password reset successful. You can now sign in." };
  }

  logout(): void {
    // Clear local storage immediately
    localStorage.removeItem("access_token");
    localStorage.removeItem("id_token");
    localStorage.removeItem("refresh_token");

    // Also sign out from Cognito (async, but we don't wait)
    getCognitoService().then(cognito => cognito.signOut()).catch(() => { });
  }

  /**
   * Decode a JWT payload with proper base64url and UTF-8 handling.
   * JWTs use base64url encoding (RFC 4648) which differs from standard base64:
   * - Uses '-' instead of '+'
   * - Uses '_' instead of '/'
   * - May omit padding '='
   * Additionally, the payload may contain UTF-8 characters (e.g., Japanese names)
   * which atob() doesn't handle correctly.
   */
  private decodeJwtPayload(token: string): Record<string, unknown> {
    const payload = token.split('.')[1];
    if (!payload) {
      throw new Error("Invalid JWT: missing payload");
    }

    // Convert base64url to standard base64
    let base64 = payload.replace(/-/g, '+').replace(/_/g, '/');

    // Add padding if needed (base64 requires length to be multiple of 4)
    const paddingNeeded = (4 - (base64.length % 4)) % 4;
    base64 += '='.repeat(paddingNeeded);

    // Decode base64 to binary string, then to UTF-8
    // atob() returns a binary string where each char is a byte (0-255)
    // For UTF-8 content, we need to properly decode the byte sequence
    const binaryString = atob(base64);
    const bytes = Uint8Array.from(binaryString, char => char.charCodeAt(0));
    const jsonString = new TextDecoder('utf-8').decode(bytes);

    return JSON.parse(jsonString);
  }

  /**
   * Sync user from Cognito to backend database
   * Sends user attributes from ID token
   */
  async syncUser(): Promise<User> {
    // Get user attributes from ID token
    const idToken = localStorage.getItem("id_token");
    let userData = {};

    if (idToken) {
      try {
        const decoded = this.decodeJwtPayload(idToken);
        userData = {
          email: decoded.email || "",
          given_name: decoded.given_name || "",
          family_name: decoded.family_name || "",
          phone_number: decoded.phone_number || "",
          email_verified: decoded.email_verified || false,
        };
      } catch (e) {
        console.warn("Failed to decode ID token:", e);
      }
    }

    return this.request<User>("/auth/sync", {
      method: "POST",
      body: JSON.stringify(userData),
    });
  }

  // User endpoints
  async getCurrentUser(): Promise<User> {
    return this.request<User>("/users/me");
  }

  // Admin endpoints
  async getAdminUsers(params: {
    page?: number;
    page_size?: number;
    search?: string;
    company?: string;
    sort?: string;
    sort_dir?: string;
  } = {}): Promise<{ users: User[]; total: number; page: number; page_size: number; company_names: string[] }> {
    const query = new URLSearchParams();
    if (params.page) query.set("page", String(params.page));
    if (params.page_size) query.set("page_size", String(params.page_size));
    if (params.search) query.set("search", params.search);
    if (params.company) query.set("company", params.company);
    if (params.sort) query.set("sort", params.sort);
    if (params.sort_dir) query.set("sort_dir", params.sort_dir);
    const qs = query.toString();
    return this.request<{ users: User[]; total: number; page: number; page_size: number; company_names: string[] }>(
      `/admin/users${qs ? `?${qs}` : ""}`
    );
  }

  async createAdminUser(data: any): Promise<User> {
    return this.request<User>('/admin/users', {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateAdminUser(userId: string, updates: any): Promise<any> {
    return this.request<any>(`/admin/users/${userId}`, {
      method: "PUT",
      body: JSON.stringify(updates),
    });
  }

  async deleteAdminUser(userId: string, permanent: boolean = false): Promise<void> {
    return this.request<void>(`/admin/users/${userId}?permanent=${permanent}`, {
      method: "DELETE",
    });
  }

  // Project endpoints
  async getProjects(): Promise<Project[]> {
    return this.request<Project[]>("/projects");
  }

  async createProject(project: CreateProjectRequest): Promise<Project> {
    return this.request<Project>("/projects", {
      method: "POST",
      body: JSON.stringify(project),
    });
  }

  async getProject(id: string): Promise<Project> {
    return this.request<Project>(`/projects/${id}`);
  }

  async updateProject(
    id: string,
    project: Partial<CreateProjectRequest>,
  ): Promise<Project> {
    return this.request<Project>(`/projects/${id}`, {
      method: "POST",
      body: JSON.stringify(project),
    });
  }

  async deleteProject(id: string): Promise<void> {
    return this.request<void>(`/projects/${id}`, {
      method: "DELETE",
    });
  }

  // Session endpoints
  async getSessions(projectId: string): Promise<Session[]> {
    return this.request<Session[]>(`/projects/${projectId}/sessions`);
  }

  async getSession(sessionId: string): Promise<Session> {
    return this.request<Session>(`/sessions/${sessionId}`);
  }

  async pollSessions(projectId: string): Promise<Session[]> {
    return this.request<Session[]>(`/projects/${projectId}/sessions`);
  }

  async createSession(
    projectId: string,
    session: CreateSessionRequest,
  ): Promise<{
    id: string;
    upload_id: string;
    part_size: number;
    part_urls: { part_number: number; url: string }[];
    expires_in: number;
    status: string;
  }> {
    return this.request(`/projects/${projectId}/sessions`, {
      method: "POST",
      body: JSON.stringify(session),
    });
  }


  async updateSessionDetails(
    sessionId: string,
    updates: { name: string; description: string },
  ): Promise<Session> {
    return this.request<Session>(`/sessions/${sessionId}/update`, {
      method: "POST",
      body: JSON.stringify(updates),
    });
  }


  /**
   * Upload a file using S3 multipart upload.
   * Splits the file into chunks, uploads each to a presigned URL,
   * and returns the list of {PartNumber, ETag} for completion.
   */
  async uploadPdfFile(
    file: File,
    partSize: number,
    partUrls: { part_number: number; url: string }[],
  ): Promise<{ PartNumber: number; ETag: string }[]> {
    const MAX_CONCURRENT = 3;
    const MAX_RETRIES = 3;
    const parts: { PartNumber: number; ETag: string }[] = [];

    // Upload a single part using XMLHttpRequest for reliability
    const uploadPart = (
      partNumber: number,
      url: string,
      blob: Blob,
    ): Promise<{ PartNumber: number; ETag: string }> => {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", url, true);
        xhr.timeout = 0; // no timeout for large chunks

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            const etag = xhr.getResponseHeader("ETag");
            if (!etag) {
              reject(new Error(`No ETag in response for part ${partNumber}`));
              return;
            }
            resolve({ PartNumber: partNumber, ETag: etag });
          } else {
            reject(
              new Error(
                `Part ${partNumber} upload failed: ${xhr.status} ${xhr.statusText}`,
              ),
            );
          }
        };

        xhr.onerror = () =>
          reject(new Error(`Part ${partNumber} upload failed: network error`));
        xhr.send(blob);
      });
    };

    // Upload with retry
    const uploadPartWithRetry = async (
      partNumber: number,
      url: string,
      blob: Blob,
    ): Promise<{ PartNumber: number; ETag: string }> => {
      let lastError: Error | undefined;
      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        try {
          return await uploadPart(partNumber, url, blob);
        } catch (err) {
          lastError = err instanceof Error ? err : new Error(String(err));
          console.warn(
            `Part ${partNumber} attempt ${attempt}/${MAX_RETRIES} failed:`,
            lastError.message,
          );
          if (attempt < MAX_RETRIES) {
            // Exponential backoff: 1s, 2s, 4s
            await new Promise((r) =>
              setTimeout(r, Math.pow(2, attempt - 1) * 1000),
            );
          }
        }
      }
      throw lastError;
    };

    // Process all parts with limited concurrency
    let i = 0;
    while (i < partUrls.length) {
      const batch = partUrls.slice(i, i + MAX_CONCURRENT);
      const batchPromises = batch.map(({ part_number, url }) => {
        const start = (part_number - 1) * partSize;
        const end = Math.min(start + partSize, file.size);
        const blob = file.slice(start, end);
        return uploadPartWithRetry(part_number, url, blob);
      });

      const batchResults = await Promise.all(batchPromises);
      parts.push(...batchResults);
      i += MAX_CONCURRENT;
    }

    return parts.sort((a, b) => a.PartNumber - b.PartNumber);
  }

  async completeMultipartUpload(
    sessionId: string,
    uploadId: string,
    parts: { PartNumber: number; ETag: string }[],
  ): Promise<void> {
    return this.request<void>(`/sessions/${sessionId}/upload/complete`, {
      method: "POST",
      body: JSON.stringify({ upload_id: uploadId, parts }),
    });
  }

  async abortMultipartUpload(
    sessionId: string,
    uploadId: string,
  ): Promise<void> {
    return this.request<void>(`/sessions/${sessionId}/upload/abort`, {
      method: "POST",
      body: JSON.stringify({ upload_id: uploadId }),
    });
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem("access_token");
  }

  async deleteSessions(sessionIds: string[]): Promise<void> {
    const token = await this.getToken();
    const response = await fetch(`${API_BASE_URL}/sessions/bulk-delete`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ session_ids: sessionIds }),
    });

    if (!response.ok) {
      throw new Error("Failed to delete sessions");
    }
  }

  // PDF Editor endpoints
  async getSessionInputPdfBlob(sessionId: string): Promise<Blob> {
    // Step 1: Get presigned URL from backend
    const data = await this.request<{ url: string; filename: string }>(`/sessions/${sessionId}/input_pdf`);

    // Step 2: Fetch PDF directly from S3 using presigned URL
    const response = await fetch(data.url);
    if (!response.ok) {
      throw new Error(`Failed to load PDF: ${response.statusText}`);
    }
    return response.blob();
  }

  async getSessionRedactionBoxes(sessionId: string): Promise<any[]> {
    // Step 1: Get presigned URL from backend
    const data = await this.request<{ url: string }>(`/sessions/${sessionId}/redaction_boxes`);

    // Step 2: Fetch JSON directly from S3 using presigned URL
    const response = await fetch(data.url);
    if (!response.ok) {
      throw new Error(`Failed to load redaction boxes: ${response.statusText}`);
    }
    return response.json();
  }

  async applyRedactions(sessionId: string, redactionBoxes: any[], mode: 'black' | 'transparent' = 'black'): Promise<void> {
    const token = await this.getToken();
    const response = await fetch(`${API_BASE_URL}/apply-redactions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        session_id: sessionId,
        redaction_boxes: redactionBoxes,
        mode: mode
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Apply redactions failed');
    }

    // Return success - email will be sent when PDF is ready
    return;
  }

  async persistRedactions(sessionId: string, redactionBoxes: any[]): Promise<void> {
    const token = await this.getToken();
    const response = await fetch(`${API_BASE_URL}/persist-redactions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        session_id: sessionId,
        redaction_boxes: redactionBoxes
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to persist redactions: ${errorText}`);
    }
  }

  async getSessionInputPdfUrl(sessionId: string): Promise<string> {
    // Get presigned URL from backend - can be used directly for PDF viewing
    const data = await this.request<{ url: string; filename: string }>(`/sessions/${sessionId}/input_pdf`);
    return data.url;
  }

  async checkRedactedPdfExists(sessionId: string, mode: 'black' | 'transparent' = 'black'): Promise<boolean> {
    const token = await this.getToken();
    const response = await fetch(`${API_BASE_URL}/sessions/${sessionId}/redacted_pdf?mode=${mode}`, {
      method: 'HEAD',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.ok;
  }

  async downloadRedactedPdf(sessionId: string, mode: 'black' | 'transparent' = 'black'): Promise<void> {
    const token = await this.getToken();
    const response = await fetch(`${API_BASE_URL}/sessions/${sessionId}/redacted_pdf?mode=${mode}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || `Failed to download redacted PDF: ${response.statusText}`);
    }

    const data = await response.json();
    const downloadUrl = data.download_url;
    const filename = data.filename || `redacted_pdf_${Date.now()}.pdf`;

    // Fetch the PDF as a blob to ensure correct filename
    const pdfResponse = await fetch(downloadUrl);
    if (!pdfResponse.ok) {
      throw new Error('Failed to download PDF file');
    }
    const blob = await pdfResponse.blob();

    // Create a blob URL and trigger download with correct filename
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);

    // Post-download cleanup: notify backend that download is complete
    // For retention_days=0 sessions, this triggers immediate dehydration
    try {
      await fetch(`${API_BASE_URL}/sessions/${sessionId}/post_download_cleanup`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ mode }),
      });
    } catch (cleanupError) {
      console.warn('Post-download cleanup failed (non-critical):', cleanupError);
    }
  }

  async updateUser(userId: string, data: Partial<User>): Promise<void> {
    return this.request<void>(`/admin/users/${userId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async getSettings(): Promise<SystemSettings> {
    return this.request<SystemSettings>("/admin/settings");
  }

  async updateSettings(settings: Partial<SystemSettings>): Promise<void> {
    return this.request<void>("/admin/settings", {
      method: "PUT",
      body: JSON.stringify(settings),
    });
  }
}

export const apiService = new ApiService();
